/**
 * Which instrumented UI surfaces are mounted right now.
 *
 * WHY THIS EXISTS. When the browser masks a cross-origin error it gives us
 * nothing: message "Script error.", no file, no line, no stack, no error
 * object (see globalHandlers.ts). The one question a human can still act on is
 * "what was on screen when it happened", and the breadcrumb trail answers that
 * only indirectly — the last click may be seconds and several renders away
 * from the throw.
 *
 * A surface registers itself while mounted, and the masked-error path records
 * the set verbatim. That turns an unattributable "Script error." into
 * "Script error. while the map overlay was mounted and maps.googleapis.com was
 * on the page" — which is a lead, not a shrug.
 *
 * DIRECTION OF DEPENDENCY IS DELIBERATE: components depend on errorLogging,
 * never the reverse. A registry that imported the map components would make the
 * error reporter un-loadable without them.
 *
 * THIS IS NOT A REPLACEMENT FOR A REAL STACK. It is a companion to the map
 * instrumentation (MapErrorBoundary, useGeocode reporting, APIProvider
 * onError): those report OUR faults with a real stack under their own
 * fingerprint. This registry only annotates the ones the browser will never let
 * us see.
 */

/**
 * Names are a closed set on purpose — this string is stored on error events and
 * read by a human triaging them, so a typo'd free-form label would silently
 * create a second, never-matched surface.
 *
 * Every member here is WIRED to a real call site. A forward-declared name with
 * no caller would be worse than absent: its permanent absence from stored
 * events reads as "that surface was not mounted" rather than "nothing ever
 * marks it", which is the zero-vs-did-not-measure confusion this whole change
 * exists to remove. Add a member in the same commit that marks it.
 *
 * Two qualify, and deliberately so: `map_overlay` mounts the Maps JS React
 * components and `places_autocomplete` (AddressAutocomplete) calls the Places
 * library, which is the code whose throws the browser redacts. They are told
 * apart on purpose: of 108 masked "Script error." events, 107 had the map
 * overlay mounted, so a Places-only event is the case the overlay mark alone
 * could not separate. `MapPreviewTooltip` renders a STATIC MAP IMAGE and
 * executes no third-party script, so listing it would imply a suspect it
 * cannot be.
 */
export type InstrumentedSurface = 'map_overlay' | 'places_autocomplete';

/**
 * Counted, not a Set of booleans: two map overlays can be mounted at once (a
 * lead detail panel and an expanded panel mid-swap), and the second one
 * unmounting must not erase the first one's mark.
 */
const mountCounts = new Map<InstrumentedSurface, number>();

/**
 * Mark a surface mounted. Returns the unmount function — the caller is expected
 * to use it as a `useEffect` cleanup, which is what guarantees the pairing.
 *
 * Never throws: this runs inside render/effect paths of real features, and a
 * telemetry annotation must never be able to break the feature it annotates.
 */
export function markSurfaceMounted(surface: InstrumentedSurface): () => void {
  mountCounts.set(surface, (mountCounts.get(surface) ?? 0) + 1);
  let released = false;
  return () => {
    // Idempotent: React can invoke a cleanup more than once in StrictMode, and
    // a double-decrement would take the count negative and un-mark a surface
    // that is still on screen.
    if (released) return;
    released = true;
    const next = (mountCounts.get(surface) ?? 1) - 1;
    if (next <= 0) mountCounts.delete(surface);
    else mountCounts.set(surface, next);
  };
}

/** The surfaces mounted right now, sorted for a stable stored value. */
export function getMountedSurfaces(): InstrumentedSurface[] {
  return Array.from(mountCounts.keys()).sort();
}

/** Tests only. Module state is a singleton exactly like the throttle counters. */
export function __resetSurfaceRegistryForTests(): void {
  mountCounts.clear();
}
